import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import { CustomerPolicyCard } from '@/components/insurance/CustomerPolicyCard';
import { CompanyInsuranceTypesTab } from '@/components/insurance/CompanyInsuranceTypesTab';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { ICompanyEmployee, ICompanyMember } from '@/@types/company';
import {
  ArrowLeft,
  AlertCircle,
  Building2,
  Mail,
  Phone,
  MapPin,
  Users,
  ClipboardList,
  Shield,
  Plus,
} from 'lucide-react';

// LOCAL_TYPE_JUSTIFIED: Scoped policy shape used only by CompanyPoliciesTab sub-component
type TPolicyItem = {
  _id: Id<'insurancePolicies'>;
  policyNumber: string;
  insuranceTypeName?: string;
  status: string;
  issuedAt?: number;
  expiresAt?: number;
  createdAt: number;
};

const roleColors: Record<string, string> = {
  admin:
    'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
  manager:
    'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
  employee:
    'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
  accountant:
    'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800',
};

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function CompanyEmployeesTab({
  employees,
  companyId,
}: {
  employees: ICompanyEmployee[];
  companyId: string;
}) {
  const navigate = useNavigate();

  return (
    <div className="bg-table-container border border-table-container-border rounded-none overflow-hidden">
      <div className="flex items-center px-6 py-2 border-b border-table-row-border bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wider">
        <span className="w-48 shrink-0">Name</span>
        <span className="w-52 shrink-0">Email</span>
        <span className="w-36 shrink-0">Phone</span>
        <span className="w-28 shrink-0">Position</span>
        <span className="w-20 shrink-0">Policies</span>
        <span className="flex-1" />
      </div>
      {employees.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Users className="w-10 h-10 text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">No employees added yet.</p>
        </div>
      )}
      {employees.map(emp => (
        <div
          key={emp._id}
          className="flex items-center px-6 py-3 border-b border-table-row-border hover:bg-muted/50 cursor-pointer"
          onClick={() => navigate(`/insurance/companies/${companyId}/employees/${emp._id}`)}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`/insurance/companies/${companyId}/employees/${emp._id}`);
            }
          }}
        >
          <span className="w-48 shrink-0 text-sm font-medium text-foreground truncate pr-3">
            {emp.name}
          </span>
          <span className="w-52 shrink-0 text-sm text-muted-foreground truncate pr-3">
            {emp.email ?? '—'}
          </span>
          <span className="w-36 shrink-0 text-sm text-muted-foreground truncate pr-3">
            {emp.phoneNumber ?? '—'}
          </span>
          <span className="w-28 shrink-0 text-sm text-muted-foreground truncate pr-3">
            {emp.position ?? '—'}
          </span>
          <span className="w-20 shrink-0 text-sm text-muted-foreground">
            {emp.policyCount ?? 0}
          </span>
          <div className="flex-1 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={e => {
                e.stopPropagation();
                navigate(
                  `/insurance/policies/new?employeeId=${emp._id}&companyId=${companyId}&source=company_employer`
                );
              }}
              aria-label={`Create policy for ${emp.name}`}
            >
              <Plus className="w-4 h-4 mr-1" aria-hidden="true" />
              Create Policy
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function CompanyMembersTab({ members }: { members: ICompanyMember[] }) {
  return (
    <div className="bg-table-container border border-table-container-border rounded-none overflow-hidden">
      <div className="flex items-center px-6 py-2 border-b border-table-row-border bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wider">
        <span className="w-48 shrink-0">Name</span>
        <span className="w-52 shrink-0">Email</span>
        <span className="flex-1">Roles</span>
      </div>
      {members.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Shield className="w-10 h-10 text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">No team members yet.</p>
        </div>
      )}
      {members.map(member => (
        <div
          key={member._id}
          className="flex items-center px-6 py-3 border-b border-table-row-border hover:bg-muted/50"
        >
          <span className="w-48 shrink-0 text-sm font-medium text-foreground truncate pr-3">
            {member.userName ?? '—'}
          </span>
          <span className="w-52 shrink-0 text-sm text-muted-foreground truncate pr-3">
            {member.userEmail ?? '—'}
          </span>
          <div className="flex-1 flex gap-1.5 flex-wrap">
            {member.roles.map(role => (
              <Badge
                key={role}
                className={cn('border-transparent', roleColors[role] ?? roleColors.employee)}
              >
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </Badge>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function CompanyPoliciesTab({ policies }: { policies: TPolicyItem[] }) {
  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        <ClipboardList className="w-4 h-4 text-muted-foreground" />
        <h2 className="text-sm font-medium text-foreground">Policies ({policies.length})</h2>
      </div>
      {policies.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <p className="text-sm">No policies linked to this company&apos;s employees yet.</p>
        </div>
      )}
      <div className="space-y-4">
        {policies.map(policy => {
          const startDate = policy.issuedAt
            ? new Date(policy.issuedAt).toISOString().split('T')[0]
            : undefined;
          const expiryDate = policy.expiresAt
            ? new Date(policy.expiresAt).toISOString().split('T')[0]
            : undefined;
          return (
            <CustomerPolicyCard
              key={policy._id}
              policyId={policy._id}
              policyNumber={policy.policyNumber}
              insuranceTypeName={policy.insuranceTypeName}
              status={policy.status}
              startDate={startDate}
              expiryDate={expiryDate}
            />
          );
        })}
      </div>
    </>
  );
}

export function CompanyDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const company = useQuery(
    api.companies.queries.get,
    id ? { companyId: id as Id<'companies'> } : 'skip' // AS_JUSTIFIED: one-time Id cast from route param
  );
  const employees = useQuery(
    api.companies.employees.list,
    id ? { companyId: id as Id<'companies'> } : 'skip' // AS_JUSTIFIED: one-time Id cast from route param
  );
  const members = useQuery(
    api.companies.members.list,
    id ? { companyId: id as Id<'companies'> } : 'skip' // AS_JUSTIFIED: one-time Id cast from route param
  );

  const policiesData = useQuery(
    api['insurance/policies'].listByCompany,
    id ? { companyId: id as Id<'companies'> } : 'skip' // AS_JUSTIFIED: one-time Id cast from route param
  );
  const policies = (policiesData ?? []) as TPolicyItem[]; // AS_JUSTIFIED: Convex query return matches TPolicyItem shape

  const isLoading = company === undefined || employees === undefined || members === undefined;

  const handleBack = useCallback(() => {
    navigate('/insurance/companies');
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-full bg-background-secondary p-8">
        <div className="flex items-center gap-3 mb-6">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-7 w-48" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-full bg-background-secondary flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-foreground mb-1">Company not found</h2>
          <p className="text-sm text-muted-foreground mb-4">
            This company may have been deleted or you don&apos;t have access.
          </p>
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Companies
          </Button>
        </div>
      </div>
    );
  }

  const statusVarMap: Record<string, string> = {
    pending: 'pending',
    active: 'active',
    archived: 'archived',
  };
  const statusVar = statusVarMap[company.status] ?? 'active';

  return (
    <div className="min-h-full bg-background-secondary p-8">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h1 className="text-xl font-semibold text-foreground">{company.name}</h1>
        <Badge
          style={{
            backgroundColor: `var(--status-${statusVar}-bg)`,
            color: `var(--status-${statusVar})`,
            borderColor: `var(--status-${statusVar}-border)`,
          }}
        >
          {company.status.charAt(0).toUpperCase() + company.status.slice(1)}
        </Badge>
      </div>

      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <div className="rounded-lg bg-muted p-2">
              <Building2 className="w-4 h-4 text-muted-foreground" />
            </div>
            Company Info
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {company.email && (
            <div className="flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-foreground truncate">{company.email}</span>
            </div>
          )}
          {company.phoneNumber && (
            <div className="flex items-center gap-2 text-sm">
              <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-foreground">{company.phoneNumber}</span>
            </div>
          )}
          {company.address && (
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-foreground truncate">{company.address}</span>
            </div>
          )}
          <div className="text-sm">
            <span className="text-muted-foreground">Created: </span>
            <span className="text-foreground">{formatDate(company.createdAt)}</span>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="employees">
        <TabsList>
          <TabsTrigger value="employees">Employees ({employees.length})</TabsTrigger>
          <TabsTrigger value="members">Team Members ({members.length})</TabsTrigger>
          <TabsTrigger value="policies">Policies ({policies.length})</TabsTrigger>
          <TabsTrigger value="insurance-types">Insurance Types</TabsTrigger>
        </TabsList>
        <TabsContent value="employees" className="mt-4">
          <CompanyEmployeesTab employees={employees} companyId={id!} />
        </TabsContent>
        <TabsContent value="members" className="mt-4">
          <CompanyMembersTab members={members} />
        </TabsContent>
        <TabsContent value="policies" className="mt-4">
          <CompanyPoliciesTab policies={policies} />
        </TabsContent>
        <TabsContent value="insurance-types" className="mt-4">
          <CompanyInsuranceTypesTab
            companyId={company._id}
            orgId={company.orgId}
            allowedInsuranceTypeIds={company.allowedInsuranceTypeIds}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
